import React from 'react';
import FieldTypeahead from './shared/FieldTypeahead';

const StringManipulationRuleForm = ({ configuration, onChange, errors, warnings, availableFields }) => {
  const operations = [
    { value: 'LEFT', label: 'LEFT - Extract first N characters', hasLength: true },
    { value: 'RIGHT', label: 'RIGHT - Extract last N characters', hasLength: true },
    { value: 'SUBSTRING', label: 'SUBSTRING - Extract substring', hasStart: true, hasLength: true },
    { value: 'TRIM', label: 'TRIM - Remove whitespace', hasNone: true },
    { value: 'UPPER', label: 'UPPER - Convert to uppercase', hasNone: true },
    { value: 'LOWER', label: 'LOWER - Convert to lowercase', hasNone: true },
    { value: 'CONCAT', label: 'CONCAT - Concatenate fields', hasFields: true, hasSeparator: true },
    { value: 'REPLACE', label: 'REPLACE - Replace text', hasSearch: true, hasReplace: true },
    { value: 'REPLACE_REGEX', label: 'REPLACE_REGEX - Replace with regex pattern', hasPattern: true, hasReplacement: true },
    { value: 'SPLIT', label: 'SPLIT - Split by delimiter', hasDelimiter: true, hasIndex: true },
    { value: 'PAD_LEFT', label: 'PAD_LEFT - Pad on the left', hasTotalWidth: true, hasPaddingChar: true },
    { value: 'PAD_RIGHT', label: 'PAD_RIGHT - Pad on the right', hasTotalWidth: true, hasPaddingChar: true },
    { value: 'YEAR', label: 'YEAR - Extract year from date', hasNone: true },
    { value: 'MONTH', label: 'MONTH - Extract month from date', hasNone: true },
    { value: 'DAY', label: 'DAY - Extract day from date', hasNone: true },
    { value: 'DATEFORMAT', label: 'DATEFORMAT - Format date', hasFormat: true }
  ];

  const handleChange = (field, value) => {
    const newConfig = { ...configuration };
    
    if (field === 'operation') {
      // Reset parameters when operation changes
      newConfig.operation = value;
      newConfig.parameters = {};
    } else if (field.startsWith('parameters.')) {
      const paramName = field.split('.')[1];
      if (!newConfig.parameters) {
        newConfig.parameters = {};
      }
      
      // Convert to appropriate type
      if (paramName === 'length' || paramName === 'start') {
        newConfig.parameters[paramName] = parseInt(value) || 0;
      } else if (paramName === 'fields') {
        // Split comma-separated fields into array
        newConfig.parameters[paramName] = value.split(',').map(f => f.trim()).filter(f => f);
      } else {
        newConfig.parameters[paramName] = value;
      }
    } else {
      newConfig[field] = value;
    }
    
    onChange(newConfig);
  };

  const getSelectedOperation = () => {
    return operations.find(op => op.value === configuration.operation);
  };

  const selectedOp = getSelectedOperation();

  return (
    <div>
      <div className="mb-3">
        <label htmlFor="sourceField" className="form-label">
          Source Field <span className="text-danger">*</span>
        </label>
        <FieldTypeahead
          id="sourceField"
          value={configuration.sourceField || ''}
          onChange={(val) => handleChange('sourceField', val)}
          suggestions={availableFields || []}
          placeholder="e.g., gl_account_number"
          error={errors?.sourceField}
          warning={warnings?.sourceField}
        />
        <small className="form-text text-muted">
          The field to read the value from
        </small>
      </div>

      <div className="mb-3">
        <label htmlFor="targetField" className="form-label">
          Target Field <span className="text-danger">*</span>
        </label>
        <FieldTypeahead
          id="targetField"
          value={configuration.targetField || ''}
          onChange={(val) => handleChange('targetField', val)}
          suggestions={availableFields || []}
          placeholder="e.g., department_code"
          error={errors?.targetField}
          warning={warnings?.targetField}
        />
        <small className="form-text text-muted">
          The field to store the result in
        </small>
      </div>

      <div className="mb-3">
        <label htmlFor="operation" className="form-label">
          Operation <span className="text-danger">*</span>
        </label>
        <select
          id="operation"
          className={`form-select ${errors?.operation ? 'is-invalid' : ''}`}
          value={configuration.operation || ''}
          onChange={(e) => handleChange('operation', e.target.value)}
          required
        >
          <option value="">Select Operation</option>
          {operations.map(op => (
            <option key={op.value} value={op.value}>
              {op.label}
            </option>
          ))}
        </select>
        {errors?.operation && <div className="invalid-feedback">{errors.operation}</div>}
        {warnings?.operation && <small className="text-warning">{warnings.operation}</small>}
      </div>

      {/* Operation-specific parameters */}
      {errors?.parameters && (
        <div className="alert alert-danger">{errors.parameters}</div>
      )}
      {selectedOp && (
        <div className="card mb-3">
          <div className="card-header">
            <strong>Operation Parameters</strong>
          </div>
          <div className="card-body">
            {selectedOp.hasLength && (
              <div className="mb-3">
                <label htmlFor="paramLength" className="form-label">
                  Length <span className="text-danger">*</span>
                </label>
                <input
                  type="number"
                  id="paramLength"
                  className="form-control"
                  value={configuration.parameters?.length || ''}
                  onChange={(e) => handleChange('parameters.length', e.target.value)}
                  min="1"
                  required
                />
                <small className="form-text text-muted">
                  Number of characters to extract
                </small>
              </div>
            )}

            {selectedOp.hasStart && (
              <div className="mb-3">
                <label htmlFor="paramStart" className="form-label">
                  Start Position <span className="text-danger">*</span>
                </label>
                <input
                  type="number"
                  id="paramStart"
                  className="form-control"
                  value={configuration.parameters?.start || ''}
                  onChange={(e) => handleChange('parameters.start', e.target.value)}
                  min="0"
                  required
                />
                <small className="form-text text-muted">
                  Starting position (0-based index)
                </small>
              </div>
            )}

            {selectedOp.hasFields && (
              <div className="mb-3">
                <label htmlFor="paramFields" className="form-label">
                  Fields to Concatenate <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  id="paramFields"
                  className="form-control"
                  value={configuration.parameters?.fields?.join(', ') || ''}
                  onChange={(e) => handleChange('parameters.fields', e.target.value)}
                  placeholder="field1, field2, field3"
                  required
                />
                {availableFields && availableFields.length > 0 && (
                  <div className="mt-1">
                    <small className="text-muted">Available fields: </small>
                    {availableFields.slice(0, 10).map(field => (
                      <button
                        key={field.value}
                        type="button"
                        className="btn btn-sm btn-outline-secondary me-1 mb-1"
                        style={{ fontSize: '0.75rem', padding: '0.1rem 0.4rem' }}
                        onClick={() => {
                          const currentFields = configuration.parameters?.fields?.join(', ') || '';
                          const separator = currentFields && !currentFields.endsWith(',') ? ', ' : '';
                          handleChange('parameters.fields', currentFields + separator + field.value);
                        }}
                      >
                        {field.value}
                      </button>
                    ))}
                  </div>
                )}
                <small className="form-text text-muted">
                  Comma-separated list of field names
                </small>
              </div>
            )}

            {selectedOp.hasSeparator && (
              <div className="mb-3">
                <label htmlFor="paramSeparator" className="form-label">
                  Separator
                </label>
                <input
                  type="text"
                  id="paramSeparator"
                  className="form-control"
                  value={configuration.parameters?.separator || ''}
                  onChange={(e) => handleChange('parameters.separator', e.target.value)}
                  placeholder="e.g., - or space"
                />
                <small className="form-text text-muted">
                  Text to insert between concatenated fields (leave empty for no separator)
                </small>
              </div>
            )}

            {selectedOp.hasSearch && (
              <div className="mb-3">
                <label htmlFor="paramSearch" className="form-label">
                  Search Text <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  id="paramSearch"
                  className="form-control"
                  value={configuration.parameters?.search || ''}
                  onChange={(e) => handleChange('parameters.search', e.target.value)}
                  placeholder="Text to find"
                  required
                />
              </div>
            )}

            {selectedOp.hasReplace && (
              <div className="mb-3">
                <label htmlFor="paramReplace" className="form-label">
                  Replace With <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  id="paramReplace"
                  className="form-control"
                  value={configuration.parameters?.replace || ''}
                  onChange={(e) => handleChange('parameters.replace', e.target.value)}
                  placeholder="Replacement text"
                  required
                />
              </div>
            )}

            {selectedOp.hasFormat && (
              <div className="mb-3">
                <label htmlFor="paramFormat" className="form-label">
                  Date Format <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  id="paramFormat"
                  className="form-control"
                  value={configuration.parameters?.format || ''}
                  onChange={(e) => handleChange('parameters.format', e.target.value)}
                  placeholder="e.g., YYMMDD, MMDDYYYY, YYYY-MM-DD"
                  required
                />
                <small className="form-text text-muted">
                  Format pattern: YYYY (4-digit year), YY (2-digit year), MM (month), DD (day)
                </small>
              </div>
            )}

            {selectedOp.hasNone && (
              <div className="alert alert-info">
                This operation does not require additional parameters.
              </div>
            )}

            {selectedOp.hasPattern && (
              <div className="mb-3">
                <label htmlFor="paramPattern" className="form-label">
                  Regex Pattern <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  id="paramPattern"
                  className="form-control"
                  value={configuration.parameters?.pattern || ''}
                  onChange={(e) => handleChange('parameters.pattern', e.target.value)}
                  placeholder="e.g., \\d+ or [A-Z]{3}"
                  required
                />
                <small className="form-text text-muted">
                  Regular expression pattern to match
                </small>
              </div>
            )}

            {selectedOp.hasReplacement && (
              <div className="mb-3">
                <label htmlFor="paramReplacement" className="form-label">
                  Replacement
                </label>
                <input
                  type="text"
                  id="paramReplacement"
                  className="form-control"
                  value={configuration.parameters?.replacement || ''}
                  onChange={(e) => handleChange('parameters.replacement', e.target.value)}
                  placeholder="Replacement text (supports $1, $2 group references)"
                />
              </div>
            )}

            {selectedOp.hasDelimiter && (
              <div className="mb-3">
                <label htmlFor="paramDelimiter" className="form-label">
                  Delimiter <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  id="paramDelimiter"
                  className="form-control"
                  value={configuration.parameters?.delimiter || ''}
                  onChange={(e) => handleChange('parameters.delimiter', e.target.value)}
                  placeholder="e.g., - or , or space"
                  required
                />
                <small className="form-text text-muted">
                  Character(s) to split the string on
                </small>
              </div>
            )}

            {selectedOp.hasIndex && (
              <div className="mb-3">
                <label htmlFor="paramIndex" className="form-label">
                  Index
                </label>
                <input
                  type="number"
                  id="paramIndex"
                  className="form-control"
                  value={configuration.parameters?.index ?? ''}
                  onChange={(e) => handleChange('parameters.index', e.target.value)}
                  min="0"
                  placeholder="Leave empty to return all parts"
                />
                <small className="form-text text-muted">
                  Zero-based index of the part to return. Leave empty to return all parts joined by comma.
                </small>
              </div>
            )}

            {selectedOp.hasTotalWidth && (
              <div className="mb-3">
                <label htmlFor={`param${configuration.operation}TotalWidth`} className="form-label">
                  Total Width <span className="text-danger">*</span>
                </label>
                <input
                  type="number"
                  id={`param${configuration.operation}TotalWidth`}
                  className="form-control"
                  value={configuration.parameters?.totalWidth || ''}
                  onChange={(e) => handleChange('parameters.totalWidth', e.target.value)}
                  min="1"
                  required
                />
                <small className="form-text text-muted">
                  Desired total length of the result string
                </small>
              </div>
            )}

            {selectedOp.hasPaddingChar && (
              <div className="mb-3">
                <label htmlFor={`param${configuration.operation}PaddingChar`} className="form-label">
                  Padding Character
                </label>
                <input
                  type="text"
                  id={`param${configuration.operation}PaddingChar`}
                  className="form-control"
                  value={configuration.parameters?.paddingChar || ''}
                  onChange={(e) => handleChange('parameters.paddingChar', e.target.value)}
                  maxLength="1"
                  placeholder="Default: space"
                />
                <small className="form-text text-muted">
                  Single character to pad with (default is space)
                </small>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Example */}
      {configuration.operation && (
        <div className="alert alert-secondary">
          <strong>Example:</strong>
          <div className="mt-2">
            {configuration.operation === 'LEFT' && (
              <code>LEFT("{configuration.sourceField || 'source'}", {configuration.parameters?.length || 'N'}) → "{configuration.targetField || 'target'}"</code>
            )}
            {configuration.operation === 'RIGHT' && (
              <code>RIGHT("{configuration.sourceField || 'source'}", {configuration.parameters?.length || 'N'}) → "{configuration.targetField || 'target'}"</code>
            )}
            {configuration.operation === 'SUBSTRING' && (
              <code>SUBSTRING("{configuration.sourceField || 'source'}", {configuration.parameters?.start || '0'}, {configuration.parameters?.length || 'N'}) → "{configuration.targetField || 'target'}"</code>
            )}
            {configuration.operation === 'TRIM' && (
              <code>TRIM("{configuration.sourceField || 'source'}") → "{configuration.targetField || 'target'}"</code>
            )}
            {configuration.operation === 'UPPER' && (
              <code>UPPER("{configuration.sourceField || 'source'}") → "{configuration.targetField || 'target'}"</code>
            )}
            {configuration.operation === 'LOWER' && (
              <code>LOWER("{configuration.sourceField || 'source'}") → "{configuration.targetField || 'target'}"</code>
            )}
            {configuration.operation === 'CONCAT' && (
              <code>CONCAT([{configuration.parameters?.fields?.join(', ') || 'field1, field2'}], "{configuration.parameters?.separator || ''}") → "{configuration.targetField || 'target'}"</code>
            )}
            {configuration.operation === 'REPLACE' && (
              <code>REPLACE("{configuration.sourceField || 'source'}", "{configuration.parameters?.search || 'find'}", "{configuration.parameters?.replace || 'replace'}") → "{configuration.targetField || 'target'}"</code>
            )}
            {configuration.operation === 'REPLACE_REGEX' && (
              <code>REPLACE_REGEX("{configuration.sourceField || 'source'}", "{configuration.parameters?.pattern || '\\d+'}", "{configuration.parameters?.replacement || ''}") → "{configuration.targetField || 'target'}"</code>
            )}
            {configuration.operation === 'SPLIT' && (
              <code>SPLIT("{configuration.sourceField || 'source'}", "{configuration.parameters?.delimiter || '-'}", {configuration.parameters?.index ?? 'all'}) → "{configuration.targetField || 'target'}"</code>
            )}
            {configuration.operation === 'PAD_LEFT' && (
              <code>PAD_LEFT("{configuration.sourceField || 'source'}", {configuration.parameters?.totalWidth || 'N'}, "{configuration.parameters?.paddingChar || ' '}") → "{configuration.targetField || 'target'}"</code>
            )}
            {configuration.operation === 'PAD_RIGHT' && (
              <code>PAD_RIGHT("{configuration.sourceField || 'source'}", {configuration.parameters?.totalWidth || 'N'}, "{configuration.parameters?.paddingChar || ' '}") → "{configuration.targetField || 'target'}"</code>
            )}
            {configuration.operation === 'YEAR' && (
              <code>YEAR("{configuration.sourceField || 'invoice_date'}") → "{configuration.targetField || 'invoice_year'}"</code>
            )}
            {configuration.operation === 'MONTH' && (
              <code>MONTH("{configuration.sourceField || 'invoice_date'}") → "{configuration.targetField || 'invoice_month'}"</code>
            )}
            {configuration.operation === 'DAY' && (
              <code>DAY("{configuration.sourceField || 'invoice_date'}") → "{configuration.targetField || 'invoice_day'}"</code>
            )}
            {configuration.operation === 'DATEFORMAT' && (
              <code>DATEFORMAT("{configuration.sourceField || 'invoice_date'}", "{configuration.parameters?.format || 'YYMMDD'}") → "{configuration.targetField || 'formatted_date'}"</code>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default StringManipulationRuleForm;
